import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MediaFormatEnum } from '../../../../../general-settings/enums/mediaFormat.enum';
import { THUMB_GAP } from '../../consts/gallery.consts';

interface GalleryThumbStripStylesProps {
  mediaFormat: MediaFormatEnum;
  thumbWidth: number;
  thumbHeight: number;
  thumbRows: number;
  thumbColumns: number;
  isScrollable: boolean;
}

const TABLET_THUMB_SIZE: Record<MediaFormatEnum, { width: number; height: number }> = {
  [MediaFormatEnum.SQUARE]: { width: 386, height: 386 },
  [MediaFormatEnum.RECTANGULAR]: { width: 256, height: 387 },
};

export const useGalleryThumbStripStyles = makeStyles<GalleryThumbStripStylesProps>()(
  (
    theme: Theme,
    { mediaFormat, thumbWidth, thumbHeight, thumbRows, thumbColumns, isScrollable },
  ) => ({
    thumbStripWrapper: {
      display: 'contents',

      [theme.breakpoints.only('xs')]: {
        display: 'none',
      },

      [theme.breakpoints.between('sm', 'lg')]: {
        display: 'block',
        position: 'relative',
        flexShrink: 0,
      },
    },
    thumbStripNav: {
      [theme.breakpoints.only('xs')]: {
        display: 'none',
      },

      [theme.breakpoints.up('lg')]: {
        display: 'none',
      },
    },
    thumbStrip: {
      display: 'flex',
      flexDirection: 'row',
      flexShrink: 0,
      width: '100%',
      gap: THUMB_GAP,
      overflowX: 'auto',
      overflowY: 'hidden',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },

      [theme.breakpoints.up('lg')]: {
        display: 'grid',
        gridAutoFlow: isScrollable ? 'column' : 'row',
        gridTemplateRows: `repeat(${thumbRows}, ${thumbHeight}px)`,
        ...(isScrollable
          ? { gridAutoColumns: `${thumbWidth}px` }
          : { gridTemplateColumns: `repeat(${thumbColumns}, ${thumbWidth}px)` }),
        flex: '0 0 auto',
        justifyContent: 'flex-start',
        height: thumbHeight * thumbRows + THUMB_GAP * (thumbRows - 1),
        marginTop: THUMB_GAP,
        overflowX: isScrollable ? 'auto' : 'hidden',
        overflowY: 'hidden',
      },
    },
    thumb: {
      position: 'relative',
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      overflow: 'hidden',
      width: TABLET_THUMB_SIZE[mediaFormat].width,
      height: TABLET_THUMB_SIZE[mediaFormat].height,

      [theme.breakpoints.up('lg')]: {
        width: thumbWidth,
        height: thumbHeight,
      },
    },
    thumbPlaceholder: {
      display: 'none',
      cursor: 'default',
      pointerEvents: 'none',
      backgroundColor: theme.palette.secondary.light,

      [theme.breakpoints.up('lg')]: {
        display: 'block',
      },
    },
    thumbSelected: {
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        border: `1px solid ${theme.palette.primary.main}`,
        pointerEvents: 'none',
      },

      [theme.breakpoints.between('xs', 'lg')]: {
        '&::after': {
          display: 'none',
        },
      },
    },
  }),
);

export const useThumbMediaV2Styles = makeStyles()((theme: Theme) => ({
  mediaWrapper: {
    width: '100%',
    height: '100%',
  },
  media: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playIcon: {
    position: 'absolute',
    zIndex: 1,
    color: theme.palette.common.white,
  },
}));
